import { motion } from 'framer-motion';
import { Package, Calendar, DollarSign, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { useAuth } from '@/hooks/useAuth';

export const Orders: React.FC = () => {
  const { dashboardData, isLoading } = useAuth();

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  const orders = dashboardData?.orders || [];
  const totalSpent = orders.reduce((sum, order) => sum + order.totalPrice, 0);
  const totalOrders = orders.length;

  const getStatusBadge = (financialStatus: string, fulfillmentStatus: string) => {
    if (financialStatus === 'refunded') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-error-50 text-error-700">
          <XCircle className="w-3 h-3" />
          Refunded
        </span>
      );
    }

    if (fulfillmentStatus === 'fulfilled') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-success-50 text-success-700">
          <CheckCircle className="w-3 h-3" />
          Delivered
        </span>
      );
    }

    if (fulfillmentStatus === 'partial') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-warning-50 text-warning-700">
          <Clock className="w-3 h-3" />
          Partially Fulfilled
        </span>
      );
    }

    if (financialStatus === 'paid' || financialStatus === 'partially_paid') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
          <TrendingUp className="w-3 h-3" />
          Processing
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">
        <Clock className="w-3 h-3" />
        {fulfillmentStatus || 'Pending'}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-neutral-900 mb-2">
          Order History
        </h1>
        <p className="text-neutral-600">
          View all your past orders and their status
        </p>
      </div>

      {/* Summary Stats */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <Package className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Total Orders</p>
                  <p className="text-2xl font-bold text-neutral-900">{totalOrders}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-success-50 rounded-lg">
                  <DollarSign className="w-6 h-6 text-success-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Total Spent</p>
                  <p className="text-2xl font-bold text-neutral-900">{formatCurrency(totalSpent)}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <Calendar className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Latest Order</p>
                  <p className="text-lg font-semibold text-neutral-900">
                    {orders.length > 0 ? formatDate(orders[0].createdAt) : 'N/A'}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </motion.div>

      {/* Orders List */}
      <div className="space-y-4">
        {orders.length === 0 ? (
          <Card>
            <CardBody className="p-12 text-center">
              <Package className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                No Orders Yet
              </h3>
              <p className="text-neutral-600">
                Your order history will appear here once you make your first purchase.
              </p>
            </CardBody>
          </Card>
        ) : (
          orders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <CardBody className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-neutral-50 rounded-lg">
                          <Package className="w-6 h-6 text-neutral-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-neutral-900">
                              Order #{order.orderNumber}
                            </h3>
                            {getStatusBadge(order.financialStatus, order.fulfillmentStatus)}
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-neutral-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(order.createdAt)}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {formatCurrency(order.totalPrice)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                        onClick={() => {
                          // TODO: Implement order details modal or page
                          console.log('View order details:', order.id);
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-500 mb-1">Order ID</p>
                        <p className="font-medium text-neutral-900">#{order.id.substring(0, 8)}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 mb-1">Payment</p>
                        <p className="font-medium text-neutral-900 capitalize">
                          {order.financialStatus.replace('_', ' ')}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-500 mb-1">Fulfillment</p>
                        <p className="font-medium text-neutral-900 capitalize">
                          {order.fulfillmentStatus || 'Unfulfilled'}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-500 mb-1">Total</p>
                        <p className="font-medium text-neutral-900">{formatCurrency(order.totalPrice)}</p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Show "Load More" if we have more than 5 orders (future pagination) */}
      {orders.length >= 5 && (
        <div className="text-center">
          <button
            className="px-6 py-3 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            onClick={() => {
              // TODO: Implement pagination
              console.log('Load more orders');
            }}
          >
            Load More Orders
          </button>
        </div>
      )}
    </div>
  );
};

export default Orders;
